import _ from 'lodash';
import { sqSummaryReportStore } from '@/core/core.stores';
import {
  buildStatsSummaryError,
  extractErrorMessage,
  fetchSpcChartTable,
  fetchStatsSummaryData,
} from '@/summaryReport/summaryReport.utilities';
import { ALIGN_WEEKLY_ID } from '@/tools/statisticsSummary/statisticsSummary.constants';
import { findItem } from '@/trendData/itemStore.utilities';
import { setTrendItemColor } from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import type { GenericTableOutput } from 'generated-plugin-api/seeq';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type { FormulaLogV1 } from '@seeqdev/api/model/FormulaLogV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

type SummaryReportItemPreview = ItemSearchPreviewV1 & { color?: string };

export interface ItemSignalAndCondition {
  signal?: SummaryReportItemPreview;
  condition?: SummaryReportItemPreview;
  alignmentCondition?: SummaryReportItemPreview & {
    conditionMetadata?: { maximumDuration?: { value?: number | null; uom?: string | null } };
  };
  formula?: string;
}

async function fetchAndBuildItemPayload(item: StatisticsSummaryItem): Promise<StatisticsSummaryItem> {
  const result = await fetchStatsSummaryData(item.id);
  const { signal, condition, alignmentCondition, formula } = await fetchItemSignalAndCondition(item.id);

  const hasXbarSpcChart = !!(result as StatisticsSummaryItem)?.data?.spcCharts?.['XBAR'];
  const alignmentConditionId =
    hasXbarSpcChart && !alignmentCondition?.id ? ALIGN_WEEKLY_ID : (alignmentCondition?.id ?? '');

  const chartTable: SpcChartTablePayload = signal?.id
    ? await fetchSpcChartTablePayload(
        item,
        signal.id,
        condition?.id,
        alignmentConditionId,
        alignmentCondition?.conditionMetadata,
        formula,
      )
    : {};

  return {
    ...item,
    ...result,
    tableData: chartTable.tableData,
    warnings: chartTable.warnings,
    error: chartTable.error,
  };
}

type SpcChartTablePayload = Pick<StatisticsSummaryItem, 'tableData' | 'warnings' | 'error'>;

async function fetchSpcChartTablePayload(
  item: StatisticsSummaryItem,
  signalId: string,
  filteringConditionId: string | undefined,
  alignmentConditionId: string,
  alignmentConditionMetadata: { maximumDuration?: { value?: number | null; uom?: string | null } } | undefined,
  statsSummaryFormula: string | undefined,
): Promise<SpcChartTablePayload> {
  try {
    const { table, warningLogs } = await fetchSpcChartTable(
      item.id,
      signalId,
      filteringConditionId,
      alignmentConditionId,
      alignmentConditionMetadata,
      statsSummaryFormula,
    );
    return { tableData: table as GenericTableOutput, warnings: extractChartTableWarnings(warningLogs) };
  } catch (error) {
    const { message } = await buildStatsSummaryError(item.id, error);
    return { error: { message } };
  }
}

function extractChartTableWarnings(warningLogs?: FormulaLogV1[]): Array<{ message: string }> | undefined {
  const messages = new Set<string>();
  warningLogs?.forEach((log) => {
    Object.values(log.formulaLogEntries ?? {}).forEach((entry) => {
      entry.logDetails?.forEach((detail) => {
        if (detail.message) {
          const message = detail.message.trim();
          messages.add(message.endsWith('.') ? message : `${message}.`);
        }
      });
    });
  });
  return messages.size > 0 ? Array.from(messages).map((message) => ({ message })) : undefined;
}

export async function addStatsSummary(item: StatisticsSummaryItem): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      sqSummaryReportStore.actions.addItem(payload);
      setTrendItemColor(payload.id, payload.color);
    })
    .catch(async (error) => {
      sqSummaryReportStore.actions.addItem({ ...item, error: await buildStatsSummaryError(item.id, error) });
    })
    .finally(() => {
      sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function insertStatsSummaryAtIndex(item: StatisticsSummaryItem, index: number): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      sqSummaryReportStore.actions.insertItemAtIndex(payload, index);
      setTrendItemColor(payload.id, payload.color);
    })
    .catch(async (error) => {
      sqSummaryReportStore.actions.insertItemAtIndex(
        { ...item, error: await buildStatsSummaryError(item.id, error) },
        index,
      );
    })
    .finally(() => {
      sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function refreshStatsData(id: string): Promise<void> {
  const item = findItem(sqSummaryReportStore.getState().items, id) as StatisticsSummaryItem | undefined;
  if (!item) {
    return;
  }
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      sqSummaryReportStore.actions.updateItem(payload);
      setTrendItemColor(payload.id, payload.color);
    })
    .catch(async (error) => {
      sqSummaryReportStore.actions.updateItem({ ...item, error: await buildStatsSummaryError(item.id, error) });
    })
    .finally(() => {
      sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function fetchItemsSignalAndConditionMap(
  items: StatisticsSummaryItem[],
): Promise<Record<string, ItemSignalAndCondition>> {
  const signalAndConditionMap: Record<string, ItemSignalAndCondition> = {};

  await Promise.all(
    items.map(async (item: StatisticsSummaryItem) => {
      try {
        signalAndConditionMap[item.id] = await fetchItemSignalAndCondition(item.id);
      } catch (error) {
        // Intentionally swallowed: the summary's primary load path already reports this failure on the item
        // itself, and consumers treat a missing map entry as "no signal/condition to show".
        console.warn(
          'Failed to fetch signal and condition for statistics summary',
          item.id,
          extractErrorMessage(error),
        );
      }
    }),
  );

  return signalAndConditionMap;
}

export async function fetchItemSignalAndCondition(itemId: string): Promise<ItemSignalAndCondition> {
  const { data } = await sqFormulasApi.getItem({ id: itemId });
  const signal = _.find(data.parameters, (param) => param.name === 'signal')?.item as SummaryReportItemPreview;
  const condition = _.find(data.parameters, (param) => param.name === 'condition')?.item as SummaryReportItemPreview;
  const alignmentCondition = _.find(data.parameters, (param) => param.name === 'alignmentCondition')
    ?.item as SummaryReportItemPreview;
  return { signal, condition, alignmentCondition, formula: data.formula };
}

export async function fetchStatSummaries(): Promise<void> {
  const { items } = sqSummaryReportStore.getState();
  if (!items?.length) return;

  sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.LOADING);
  try {
    const updatedItems = await Promise.all(
      _.map(items, (item) =>
        fetchAndBuildItemPayload(item).catch(async (error) => ({
          ...item,
          error: await buildStatsSummaryError(item.id, error),
        })),
      ),
    );
    sqSummaryReportStore.actions.setItems(updatedItems);
  } finally {
    sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
  }
}
